/**
 * 아이템 창. 원작 아이템 창 배치(제목줄 + 5탭 + 슬롯 그리드 + 하단 메소)를 참고했다.
 */

import Phaser from 'phaser';
import type { AssetTable } from '../assets/loader';
import { DROPS } from '../data/balance';
import { ITEMS, TAB_LABELS, TAB_ORDER, type ItemTab } from '../data/items';
import { EV } from '../game/events';
import type { PlayerState } from '../systems/PlayerState';
import { fitText, setFitted } from './fitText';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const COLS = 4;
const ROWS = Math.ceil(DROPS.inventorySlots / COLS);
const SLOT = 42;
const GAP = 4;
const PAD = 14;
const WIDTH = Math.max(300, PAD * 2 + COLS * SLOT + (COLS - 1) * GAP + 8);
const HEADER = 30;
const TABS_H = 26;
const FOOTER = 58;
const HEIGHT = HEADER + TABS_H + PAD + ROWS * SLOT + (ROWS - 1) * GAP + FOOTER;

export class InventoryWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly icons: Phaser.GameObjects.Image[] = [];
  private readonly counts: Phaser.GameObjects.Text[] = [];
  private readonly tabTexts: Phaser.GameObjects.Text[] = [];
  private readonly tooltip: Phaser.GameObjects.Text;
  private readonly mesoText: Phaser.GameObjects.Text;
  private activeTab: ItemTab = 'use';

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
    private readonly assets: AssetTable,
  ) {
    const x = scene.scale.width - WIDTH - 24;
    const y = 70;
    this.container = scene.add.container(x, y).setVisible(false);

    this.g = scene.add.graphics();
    this.container.add(this.g);

    const title = scene.add.text(PAD, 8, '아이템 창', {
      fontFamily: UI_FONT,
      fontSize: '15px',
      color: TEXT_COLOR.bright,
    });
    this.container.add(title);

    // 닫기 버튼 (원작처럼 제목줄 오른쪽). 키(I/Esc)로도 닫을 수 있다.
    const closeBg = scene.add.rectangle(WIDTH - PAD - 8, HEADER / 2, 22, 20, COLOR.windowDark);
    closeBg.setOrigin(0.5).setInteractive({ useHandCursor: true });
    const hint = scene.add.text(WIDTH - PAD - 8, HEADER / 2, '✕', {
      fontFamily: UI_MONO,
      fontSize: '12px',
      color: TEXT_COLOR.dark,
    });
    hint.setOrigin(0.5);
    closeBg.on('pointerdown', () => this.close());
    hint.setInteractive({ useHandCursor: true }).on('pointerdown', () => this.close());
    this.container.add([closeBg, hint]);

    TAB_ORDER.forEach((tab, i) => {
      const tw = (WIDTH - PAD * 2) / TAB_ORDER.length;
      const tx = PAD + tw * i;
      const text = scene.add.text(tx + tw / 2, HEADER + TABS_H / 2, TAB_LABELS[tab], {
        fontFamily: UI_FONT,
        fontSize: '11px',
        color: TEXT_COLOR.dark,
      });
      text.setOrigin(0.5);
      fitText(text, tw - 4, 9);
      text.setInteractive({ useHandCursor: true });
      text.on('pointerdown', () => {
        this.activeTab = tab;
        this.refresh();
      });
      this.tabTexts.push(text);
      this.container.add(text);
    });

    for (let i = 0; i < DROPS.inventorySlots; i++) {
      const pos = this.slotPos(i);
      const icon = scene.add.image(pos.x + SLOT / 2, pos.y + SLOT / 2, 'item-meso');
      icon.setVisible(false).setScale(0.9);
      icon.setInteractive({ useHandCursor: true });
      icon.on('pointerover', () => this.showTooltip(i));
      icon.on('pointerout', () => this.tooltip.setText(''));
      icon.on('pointerdown', () => this.useSlot(i));
      this.icons.push(icon);
      this.container.add(icon);

      const count = scene.add.text(pos.x + SLOT - 3, pos.y + SLOT - 2, '', {
        fontFamily: UI_MONO,
        fontSize: '13px',
        fontStyle: 'bold',
        color: TEXT_COLOR.dark,
        stroke: '#f7ecd2',
        strokeThickness: 3,
      });
      count.setOrigin(1, 1);
      this.counts.push(count);
      this.container.add(count);
    }

    this.tooltip = scene.add.text(PAD, HEIGHT - FOOTER + 6, '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2 },
    });
    this.container.add(this.tooltip);

    this.mesoText = scene.add.text(WIDTH - PAD, HEIGHT - 22, '', {
      fontFamily: UI_MONO,
      fontSize: '13px',
      color: TEXT_COLOR.dark,
    });
    this.mesoText.setOrigin(1, 0.5);
    this.container.add(this.mesoText);

    this.state.inventory.events.on(EV.inventoryChanged, this.refresh, this);
    this.state.events.on(EV.mesoChanged, this.refresh, this);

    this.drawFrame();
    this.refresh();
  }

  /** 씬이 내려갈 때 구독 해제 (PlayerState 는 씬보다 오래 산다 → TS-0005) */
  destroy(): void {
    this.state.inventory.events.off(EV.inventoryChanged, this.refresh, this);
    this.state.events.off(EV.mesoChanged, this.refresh, this);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  toggle(): boolean {
    return this.setOpen(!this.container.visible);
  }

  close(): boolean {
    return this.setOpen(false);
  }

  private setOpen(open: boolean): boolean {
    this.container.setVisible(open);
    if (open) this.refresh();
    else this.tooltip.setText('');
    return open;
  }

  private slotPos(index: number): { x: number; y: number } {
    const col = index % COLS;
    const row = Math.floor(index / COLS);
    return {
      x: PAD + col * (SLOT + GAP),
      y: HEADER + TABS_H + PAD + row * (SLOT + GAP),
    };
  }

  private drawFrame(): void {
    const g = this.g;
    g.clear();
    g.fillStyle(COLOR.windowEdge, 0.95);
    g.fillRoundedRect(-3, -3, WIDTH + 6, HEIGHT + 6, 8);
    g.fillStyle(COLOR.window, 1);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 6);
    g.fillStyle(COLOR.windowEdge, 1);
    g.fillRoundedRect(0, 0, WIDTH, HEADER, 6);
    g.fillStyle(COLOR.windowDark, 1);
    g.fillRect(0, HEADER, WIDTH, TABS_H);

    for (let i = 0; i < DROPS.inventorySlots; i++) {
      const pos = this.slotPos(i);
      g.fillStyle(COLOR.slot, 1);
      g.fillRoundedRect(pos.x, pos.y, SLOT, SLOT, 4);
      g.lineStyle(1, COLOR.slotEdge, 1);
      g.strokeRoundedRect(pos.x, pos.y, SLOT, SLOT, 4);
    }

    g.lineStyle(1, COLOR.slotEdge, 1);
    g.lineBetween(PAD, HEIGHT - FOOTER, WIDTH - PAD, HEIGHT - FOOTER);
  }

  private refresh(): void {
    if (!this.container.scene) return;
    const slots = this.state.inventory.slots(this.activeTab);

    this.tabTexts.forEach((text, i) => {
      const active = TAB_ORDER[i] === this.activeTab;
      text.setColor(active ? TEXT_COLOR.dark : '#8a7550');
      text.setFontStyle(active ? 'bold' : 'normal');
    });

    slots.forEach((slot, i) => {
      const icon = this.icons[i];
      const count = this.counts[i];
      if (!slot) {
        icon.setVisible(false);
        count.setText('');
        return;
      }
      const def = ITEMS[slot.itemId];
      if (!def) {
        icon.setVisible(false);
        count.setText('');
        return;
      }
      icon.setTexture(def.texture, 0);
      icon.setScale(Math.min(1, (SLOT - 8) / this.assets.get(def.texture).frameHeight));
      icon.setVisible(true);
      count.setText(slot.count > 1 ? `${slot.count}` : '');
    });

    setFitted(this.mesoText, `${this.state.meso.toLocaleString()} 메소`, WIDTH - PAD * 2, 10);
  }

  private showTooltip(index: number): void {
    const slot = this.state.inventory.slots(this.activeTab)[index];
    if (!slot) {
      this.tooltip.setText('');
      return;
    }
    const def = ITEMS[slot.itemId];
    if (!def) return;
    const usable = def.use ? ' (클릭하면 사용)' : def.equip ? ' (클릭하면 장착)' : def.scroll ? ' (클릭 후 장비 강화)' : '';
    const extra = slot.gear
      ? `\n${slot.gear.rarity}  공+${slot.gear.watk} 방+${slot.gear.def} 명+${slot.gear.acc}  남은 횟수 ${slot.gear.upgradesLeft}`
      : '';
    this.tooltip.setText(`${def.name}\n${def.description}${usable}${extra}`);
  }

  private useSlot(index: number): void {
    const slot = this.state.inventory.slots(this.activeTab)[index];
    if (!slot) return;
    const def = ITEMS[slot.itemId];
    if (def.equip || def.scroll) {
      this.state.equipWeapon(this.activeTab, index);
      return;
    }
    if (!def?.use) {
      this.showTooltip(index);
      return;
    }
    if (this.state.useItem(this.activeTab, index)) {
      this.state.events.emit(EV.toast, { message: `${def.name} 사용`, tone: 'good' });
    }
  }
}
